import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useToast } from '../../contexts/ToastContext';
import { integrationApiService } from '../../services/integrationApi';
import type {
  IntegrationConfig,
  LightspeedCategoriesResponse,
  LightspeedCategoryOption,
  LightspeedCustomFieldOption,
  LightspeedSpecOption,
} from '../../services/integrationApi';
import { EmptyState, InlineMessage, LoadingState, SectionCard, StatusChip } from '../ui';
import { titleCaseSlug } from '../mapping/mappingModel';
import { tokens } from '../../theme/tokens';
import {
  pendingCategorySuggestions,
  pendingFieldSuggestions,
  sameCategoryMap,
  sameFieldRows,
  staleFieldRows,
} from './lightspeedMappingModel';
import type { FieldRow } from './lightspeedMappingModel';

const T = 'integrations.lightspeedConfig';

export interface LightspeedMappingPanelProps {
  integration: IntegrationConfig;
  onOpenProductFields?: () => void;
  /** The list of items whose category Motzu changed (off in the setup walkthrough, where nothing has synced). */
  showChanges?: boolean;
}

const fmtWhen = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '');

/**
 * Mapping tab for a Lightspeed integration (flag `lightspeed_store_config`): Motzu categories onto
 * the store's own Lightspeed categories, catalog specs onto its item custom fields, and the items
 * whose category Motzu changed. Suggestions never apply on their own; one save bar saves both maps.
 */
const LightspeedMappingPanel: React.FC<LightspeedMappingPanelProps> = ({
  integration,
  onOpenProductFields,
  showChanges = true,
}) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const id = integration.id;

  const categoriesQuery = useQuery({
    queryKey: ['lightspeed-categories', id],
    queryFn: () => integrationApiService.getLightspeedCategories(id),
  });
  const fieldsQuery = useQuery({
    queryKey: ['lightspeed-custom-fields', id],
    queryFn: () => integrationApiService.getLightspeedCustomFields(id),
  });
  const changesQuery = useQuery({
    queryKey: ['lightspeed-category-changes', id],
    queryFn: () => integrationApiService.getLightspeedCategoryChanges(id),
    enabled: showChanges,
  });

  const [categoryMap, setCategoryMap] = useState<Record<string, string>>({});
  const [defaultCategoryId, setDefaultCategoryId] = useState<string | null>(null);
  const [replaceCategories, setReplaceCategories] = useState(false);
  const [registryMap, setRegistryMap] = useState<Record<string, string>>({});
  const [fieldRows, setFieldRows] = useState<FieldRow[]>([]);

  const savedLayout = categoriesQuery.data?.layout;
  const savedFields = fieldsQuery.data?.mappings;
  const categoryRules = categoriesQuery.data?.categoryRules ?? null;
  const seed = () => {
    setCategoryMap(savedLayout?.categoryMap || {});
    setDefaultCategoryId(savedLayout?.defaultCategoryId ?? null);
    setReplaceCategories(savedLayout?.replaceCategories === true);
    setRegistryMap(savedLayout?.lightspeedCategoryByRegistryEntry || {});
    setFieldRows((savedFields || []).map((m) => ({ specLabel: m.specLabel, customFieldId: m.customFieldId })));
  };
  // Re-seed whenever the server copy changes (first load, save, refresh from Lightspeed).
  useEffect(seed, [savedLayout, savedFields]); // eslint-disable-line react-hooks/exhaustive-deps

  const categoriesDirty =
    !!savedLayout &&
    (!sameCategoryMap(categoryMap, savedLayout.categoryMap || {}) ||
      defaultCategoryId !== (savedLayout.defaultCategoryId ?? null) ||
      replaceCategories !== (savedLayout.replaceCategories === true) ||
      (!!categoryRules && !sameCategoryMap(registryMap, savedLayout.lightspeedCategoryByRegistryEntry || {})));
  // Fields that are gone or no longer writable are not in the table. Drop them on save so one
  // stale id cannot reject the whole mapping, and keep the bar up until that drop is stored.
  const liveFields = fieldsQuery.isSuccess ? fieldsQuery.data?.fields ?? [] : null;
  const staleRows = liveFields ? staleFieldRows(liveFields, fieldRows) : [];
  const customFieldsToSave = fieldRows.filter(
    (row) => !staleRows.some((stale) => stale.customFieldId === row.customFieldId),
  );
  const fieldsDirty = !!savedFields && !sameFieldRows(customFieldsToSave, savedFields);
  const dirty = categoriesDirty || fieldsDirty;

  const save = useMutation({
    mutationFn: () =>
      integrationApiService.saveLightspeedMapping(id, {
        ...(categoriesDirty ? { layout: { categoryMap, defaultCategoryId, replaceCategories, ...(categoryRules ? { lightspeedCategoryByRegistryEntry: registryMap } : {}) } } : {}),
        ...(fieldsDirty ? { customFields: customFieldsToSave } : {}),
      }),
    onSuccess: () => {
      showToast(t(`${T}.saved`), 'success');
      queryClient.invalidateQueries({ queryKey: ['lightspeed-categories', id] });
      queryClient.invalidateQueries({ queryKey: ['lightspeed-custom-fields', id] });
      queryClient.invalidateQueries({ queryKey: ['integration', id] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
    onError: (err: Error) => showToast(err.message || t(`${T}.saveFailed`), 'error'),
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <CategoriesCard
        loading={categoriesQuery.isLoading}
        error={categoriesQuery.error as Error | null}
        categories={categoriesQuery.data?.categories || []}
        slugs={categoriesQuery.data?.slugs || []}
        suggestions={categoriesQuery.data?.suggestions || {}}
        missingCategoryIds={categoriesQuery.data?.missingCategoryIds || []}
        categoryMap={categoryMap}
        defaultCategoryId={defaultCategoryId}
        replaceCategories={replaceCategories}
        refreshing={categoriesQuery.isFetching}
        onRefresh={() => categoriesQuery.refetch()}
        onMapChange={setCategoryMap}
        onDefaultChange={setDefaultCategoryId}
        onReplaceChange={setReplaceCategories}
        categoryRules={categoryRules}
        registryMap={registryMap}
        onRegistryMapChange={setRegistryMap}
      />
      <CustomFieldsCard
        loading={fieldsQuery.isLoading}
        error={fieldsQuery.error as Error | null}
        fields={fieldsQuery.data?.fields || []}
        specs={fieldsQuery.data?.specs || []}
        rows={fieldRows}
        onRowsChange={setFieldRows}
        onOpenProductFields={onOpenProductFields}
      />
      {showChanges && (
      <SectionCard title={t(`${T}.changes.title`)} subtitle={t(`${T}.changes.subtitle`)} dense>
        {changesQuery.isLoading ? (
          <LoadingState />
        ) : (changesQuery.data || []).length === 0 ? (
          <EmptyState size="compact" title={t(`${T}.changes.empty`)} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t(`${T}.changes.product`)}</TableCell>
                <TableCell>{t(`${T}.changes.from`)}</TableCell>
                <TableCell>{t(`${T}.changes.to`)}</TableCell>
                <TableCell>{t(`${T}.changes.why`)}</TableCell>
                <TableCell>{t(`${T}.changes.when`)}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(changesQuery.data || []).map((row) => (
                <TableRow key={row.productId}>
                  <TableCell>{row.productName || row.productId}</TableCell>
                  <TableCell>{categoryName(categoriesQuery.data?.categories, row.fromCategoryId)}</TableCell>
                  <TableCell>{categoryName(categoriesQuery.data?.categories, row.toCategoryId)}</TableCell>
                  <TableCell>
                    <StatusChip tone={row.reason === 'forced' ? 'caution' : 'info'} label={t(`${T}.changes.reason.${row.reason}`)} />
                  </TableCell>
                  <TableCell>{fmtWhen(row.at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>
      )}

      {dirty && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 16,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            px: 2,
            py: 1.25,
            bgcolor: tokens.surface.card,
            borderRadius: `${tokens.radius.card}px`,
            boxShadow: tokens.shadow.flyout,
            border: '1px solid',
            borderColor: tokens.line.default,
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tokens.signal.caution.solid, flex: '0 0 auto' }} />
          <Typography variant="body2" sx={{ color: tokens.ink.body, flex: '1 1 220px' }}>
            {t(`${T}.unsaved`)}
          </Typography>
          <Button onClick={seed} disabled={save.isPending}>
            {t(`${T}.discard`)}
          </Button>
          <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? t(`${T}.saving`) : t(`${T}.save`)}
          </Button>
        </Box>
      )}
    </Box>
  );
};

function categoryName(categories: LightspeedCategoryOption[] | undefined, categoryId: string): string {
  const hit = categories?.find((c) => c.categoryID === categoryId);
  return hit ? hit.fullPathName || hit.name : `#${categoryId}`;
}

interface CategoryPickerProps {
  categories: LightspeedCategoryOption[];
  value: string | null;
  onChange: (next: string | null) => void;
  placeholder: string;
  ariaLabel: string;
}

const CategoryPicker: React.FC<CategoryPickerProps> = ({ categories, value, onChange, placeholder, ariaLabel }) => {
  const selected = categories.find((c) => c.categoryID === value) || null;
  return (
    <Autocomplete
      size="small"
      options={categories}
      value={selected}
      onChange={(_e, next) => onChange(next ? next.categoryID : null)}
      getOptionLabel={(c) => c.fullPathName || c.name}
      isOptionEqualToValue={(a, b) => a.categoryID === b.categoryID}
      renderInput={(params) => (
        <TextField {...params} placeholder={placeholder} inputProps={{ ...params.inputProps, 'aria-label': ariaLabel }} />
      )}
      sx={{ minWidth: 260 }}
    />
  );
};

interface CategoriesCardProps {
  loading: boolean;
  error: Error | null;
  refreshing: boolean;
  categories: LightspeedCategoryOption[];
  slugs: string[];
  suggestions: Record<string, string>;
  missingCategoryIds: string[];
  categoryMap: Record<string, string>;
  defaultCategoryId: string | null;
  replaceCategories: boolean;
  onRefresh: () => void;
  onMapChange: (next: Record<string, string>) => void;
  onDefaultChange: (next: string | null) => void;
  onReplaceChange: (next: boolean) => void;
  /** Flag `lightspeed_category_rules`: null when off, which hides the notice and the registry column. */
  categoryRules: LightspeedCategoriesResponse['categoryRules'];
  registryMap: Record<string, string>;
  onRegistryMapChange: (next: Record<string, string>) => void;
}

const CategoriesCard: React.FC<CategoriesCardProps> = (props) => {
  const { t } = useAppTranslation();
  const { categories, slugs, suggestions, categoryMap } = props;
  const pending = useMemo(
    () => pendingCategorySuggestions(slugs, suggestions, categoryMap),
    [slugs, suggestions, categoryMap],
  );
  const mappedCount = slugs.filter((s) => categoryMap[s]).length;
  const setSlug = (slug: string, next: string | null) => {
    const copy = { ...categoryMap };
    if (next) copy[slug] = next;
    else delete copy[slug];
    props.onMapChange(copy);
  };

  return (
    <SectionCard
      title={t(`${T}.categories.title`)}
      subtitle={t(`${T}.categories.subtitle`)}
      actions={
        <Button size="small" variant="outlined" onClick={props.onRefresh} disabled={props.refreshing}>
          {props.refreshing ? t(`${T}.refreshing`) : t(`${T}.refresh`)}
        </Button>
      }
    >
      {props.loading ? (
        <LoadingState message={t(`${T}.categories.loading`)} />
      ) : props.error ? (
        <InlineMessage severity="error">{props.error.message}</InlineMessage>
      ) : categories.length === 0 ? (
        <EmptyState size="compact" title={t(`${T}.categories.none`)} description={t(`${T}.categories.noneBody`)} />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t(`${T}.categories.coverage`, { mapped: mappedCount, total: slugs.length, count: categories.length })}
          </Typography>
          {props.missingCategoryIds.length > 0 && (
            <InlineMessage severity="warning">
              {t(`${T}.categories.missing`, { count: props.missingCategoryIds.length })}
            </InlineMessage>
          )}
          {pending.length > 0 && (
            <InlineMessage severity="info">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <span>{t(`${T}.categories.suggestions`, { count: pending.length })}</span>
                <Button
                  size="small"
                  onClick={() => props.onMapChange({ ...categoryMap, ...Object.fromEntries(pending.map((s) => [s, suggestions[s]])) })}
                >
                  {t(`${T}.acceptAll`)}
                </Button>
              </Box>
            </InlineMessage>
          )}

          {props.categoryRules && (
            <InlineMessage severity="info">
              {t(`${T}.categories.rulesNotice`)}{' '}
              {props.categoryRules.nameRules.length > 0 &&
                t(`${T}.categories.rulesCount`, { count: props.categoryRules.nameRules.length })}
            </InlineMessage>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography variant="overline">{t(`${T}.categories.defaultLabel`)}</Typography>
            <CategoryPicker
              categories={categories}
              value={props.defaultCategoryId}
              onChange={props.onDefaultChange}
              placeholder={t(`${T}.categories.defaultPlaceholder`)}
              ariaLabel={t(`${T}.categories.defaultLabel`)}
            />
            <Typography variant="caption" color="text.secondary">
              {t(`${T}.categories.defaultHelp`)}
            </Typography>
          </Box>

          <FormControlLabel
            control={<Switch checked={props.replaceCategories} onChange={(e) => props.onReplaceChange(e.target.checked)} />}
            label={
              <Box>
                <Typography variant="body2">{t(`${T}.categories.replaceLabel`)}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {props.replaceCategories ? t(`${T}.categories.replaceOn`) : t(`${T}.categories.replaceOff`)}
                </Typography>
              </Box>
            }
          />

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t(`${T}.categories.motzu`)}</TableCell>
                <TableCell>{t(`${T}.categories.lightspeed`)}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {slugs.map((slug) => {
                const suggestion = !categoryMap[slug] ? suggestions[slug] : undefined;
                return (
                  <TableRow key={slug}>
                    <TableCell sx={{ width: '35%' }}>{titleCaseSlug(slug)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <CategoryPicker
                          categories={categories}
                          value={categoryMap[slug] || null}
                          onChange={(next) => setSlug(slug, next)}
                          placeholder={t(`${T}.categories.notMapped`)}
                          ariaLabel={titleCaseSlug(slug)}
                        />
                        {suggestion && (
                          <Button size="small" onClick={() => setSlug(slug, suggestion)}>
                            {t(`${T}.useSuggestion`, { name: categoryName(categories, suggestion) })}
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {props.categoryRules && props.categoryRules.registryEntries.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography variant="body2" color="text.secondary">
                {t(`${T}.categories.registryHelp`)}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t(`${T}.categories.registryTitle`)}</TableCell>
                    <TableCell>{t(`${T}.categories.lightspeed`)}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {props.categoryRules.registryEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell sx={{ width: '35%' }}>{entry.label}</TableCell>
                      <TableCell>
                        <CategoryPicker
                          categories={categories}
                          value={props.registryMap[entry.id] || null}
                          onChange={(next) => {
                            const copy = { ...props.registryMap };
                            if (next) copy[entry.id] = next;
                            else delete copy[entry.id];
                            props.onRegistryMapChange(copy);
                          }}
                          placeholder={t(`${T}.categories.notMapped`)}
                          ariaLabel={t(`${T}.categories.registryLabel`, { entry: entry.label })}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      )}
    </SectionCard>
  );
};

interface CustomFieldsCardProps {
  loading: boolean;
  error: Error | null;
  fields: LightspeedCustomFieldOption[];
  specs: LightspeedSpecOption[];
  rows: FieldRow[];
  onRowsChange: (next: FieldRow[]) => void;
  onOpenProductFields?: () => void;
}

const CustomFieldsCard: React.FC<CustomFieldsCardProps> = ({
  loading,
  error,
  fields,
  specs,
  rows,
  onRowsChange,
  onOpenProductFields,
}) => {
  const { t } = useAppTranslation();
  const specByField = new Map(rows.map((r) => [r.customFieldId, r.specLabel]));
  const pending = useMemo(() => pendingFieldSuggestions(fields, specs, rows), [fields, specs, rows]);
  const knownFieldIds = new Set(fields.map((field) => field.customFieldID));
  const missingRows = !loading && !error ? rows.filter((row) => !knownFieldIds.has(row.customFieldId)) : [];
  const setField = (customFieldId: string, specLabel: string | null) => {
    const rest = rows.filter((r) => r.customFieldId !== customFieldId);
    onRowsChange(specLabel ? [...rest, { customFieldId, specLabel }] : rest);
  };

  return (
    <SectionCard
      title={t(`${T}.fields.title`)}
      subtitle={t(`${T}.fields.subtitle`)}
      actions={
        onOpenProductFields ? (
          <Button size="small" variant="outlined" onClick={onOpenProductFields}>
            {t(`${T}.fields.create`)}
          </Button>
        ) : undefined
      }
    >
      {loading ? (
        <LoadingState message={t(`${T}.fields.loading`)} />
      ) : error ? (
        <InlineMessage severity="error">{error.message}</InlineMessage>
      ) : fields.length === 0 && missingRows.length === 0 ? (
        <EmptyState
          size="compact"
          title={t(`${T}.fields.none`)}
          description={t(`${T}.fields.noneBody`)}
          actionLabel={onOpenProductFields ? t(`${T}.fields.create`) : undefined}
          onAction={onOpenProductFields}
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {pending.length > 0 && (
            <InlineMessage severity="info">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <span>{t(`${T}.fields.suggestions`, { count: pending.length })}</span>
                <Button size="small" onClick={() => onRowsChange([...rows, ...pending])}>
                  {t(`${T}.acceptAll`)}
                </Button>
              </Box>
            </InlineMessage>
          )}
          {fields.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t(`${T}.fields.field`)}</TableCell>
                <TableCell>{t(`${T}.fields.spec`)}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((field) => {
                const current = specByField.get(field.customFieldID) || null;
                const suggestion = pending.find((p) => p.customFieldId === field.customFieldID);
                return (
                  <TableRow key={field.customFieldID}>
                    <TableCell sx={{ width: '35%' }}>
                      <Typography variant="body2">{field.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {field.type}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {!field.writable ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary">
                            {current
                              ? t(`${T}.fields.staleNotWritable`, { type: field.type })
                              : t(`${T}.fields.notWritable`, { type: field.type })}
                          </Typography>
                          {current && (
                            <>
                              <StatusChip tone="caution" label={t(`${T}.fields.staleSpec`, { spec: current })} />
                              <Button size="small" onClick={() => setField(field.customFieldID, null)}>
                                {t(`${T}.fields.remove`)}
                              </Button>
                            </>
                          )}
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Autocomplete
                            size="small"
                            options={specs.map((s) => s.label)}
                            value={current}
                            onChange={(_e, next) => setField(field.customFieldID, next)}
                            getOptionLabel={(label) => {
                              const spec = specs.find((s) => s.label === label);
                              return spec ? `${label} (${spec.productCount})` : label;
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={t(`${T}.fields.notMapped`)}
                                inputProps={{ ...params.inputProps, 'aria-label': field.name }}
                              />
                            )}
                            sx={{ minWidth: 260 }}
                          />
                          {suggestion && (
                            <Button size="small" onClick={() => setField(field.customFieldID, suggestion.specLabel)}>
                              {t(`${T}.useSuggestion`, { name: suggestion.specLabel })}
                            </Button>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          )}
          {missingRows.map((row) => (
            <InlineMessage key={row.customFieldId} severity="warning">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <span>
                  {t(`${T}.fields.staleMissing`)} {t(`${T}.fields.staleSpec`, { spec: row.specLabel })}
                </span>
                <Button size="small" onClick={() => setField(row.customFieldId, null)}>
                  {t(`${T}.fields.remove`)}
                </Button>
              </Box>
            </InlineMessage>
          ))}
        </Box>
      )}
    </SectionCard>
  );
};

export default LightspeedMappingPanel;
